import { useEffect, useState } from 'react'
import { CheckCircle2, Info, TriangleAlert, X, XCircle } from 'lucide-react'
import { getCurrentSession } from '@/lib/auth'

export type ToastColor = 'success' | 'danger' | 'warning' | 'primary' | 'info'

export interface ToastInput {
  title: string
  description?: string
  color?: ToastColor
  /** Accepted for backwards-compat with the old HeroUI API; ignored. */
  severity?: string
}

interface ToastItem extends ToastInput {
  id: number
}

type Listener = (toast: ToastItem) => void

let counter = 0
const listeners = new Set<Listener>()

/**
 * Show a toast notification. Safe to call from anywhere (including outside the
 * React tree) — it forwards to whichever <ToastProvider/> is mounted.
 */
export function toast(input: ToastInput) {
  const item: ToastItem = { id: ++counter, ...input }
  listeners.forEach((l) => l(item))
}

/**
 * Show a danger toast for a failed request. The raw error details are only
 * surfaced to admins — regular users just see the title.
 */
export function errorToast(title: string, error?: unknown) {
  toast({
    color: 'danger',
    title,
    description: getCurrentSession()?.admin ? formatError(error) : undefined,
  })
}

interface HumaErrorModel {
  detail?: string
  title?: string
  status?: number
  errors?: Array<{ location?: string; message?: string }> | null
}

function formatError(error: unknown): string | undefined {
  if (error === undefined || error === null) return undefined
  if (typeof error === 'string') return error || undefined
  if (error instanceof Error) return error.message
  if (typeof error !== 'object') return String(error)

  const e = error as HumaErrorModel
  const parts: Array<string> = []
  const head = e.detail ?? e.title
  if (e.status && head) parts.push(`[${e.status}] ${head}`)
  else if (e.status) parts.push(`[${e.status}]`)
  else if (head) parts.push(head)
  if (e.errors?.length) {
    const list = e.errors
      .map((d) => [d.location, d.message].filter(Boolean).join(': '))
      .filter(Boolean)
      .join(' ; ')
    if (list) parts.push(list)
  }
  if (parts.length) return parts.join(' — ')
  try {
    return JSON.stringify(error)
  } catch {
    return String(error)
  }
}

const TONE: Record<
  ToastColor,
  { bg: string; border: string; fg: string; icon: React.ReactNode }
> = {
  success: {
    bg: 'var(--success-tint)',
    border: 'var(--success)',
    fg: 'var(--emerald-800)',
    icon: <CheckCircle2 size={18} />,
  },
  primary: {
    bg: 'var(--surface-tint)',
    border: 'var(--emerald-600)',
    fg: 'var(--emerald-800)',
    icon: <CheckCircle2 size={18} />,
  },
  info: {
    bg: 'var(--info-tint)',
    border: 'var(--info)',
    fg: 'var(--ink-1)',
    icon: <Info size={18} />,
  },
  warning: {
    bg: 'var(--warning-tint)',
    border: 'var(--warning)',
    fg: 'var(--ink-1)',
    icon: <TriangleAlert size={18} />,
  },
  danger: {
    bg: 'var(--danger-tint)',
    border: 'var(--danger)',
    fg: 'var(--danger)',
    icon: <XCircle size={18} />,
  },
}

export function ToastProvider() {
  const [toasts, setToasts] = useState<Array<ToastItem>>([])

  useEffect(() => {
    const listener: Listener = (item) => {
      setToasts((prev) => [...prev, item])
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== item.id))
      }, 5000)
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  const dismiss = (id: number) =>
    setToasts((prev) => prev.filter((t) => t.id !== id))

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 'var(--space-6)',
        right: 'var(--space-6)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        maxWidth: 'min(92vw, 380px)',
      }}
    >
      {toasts.map((t) => {
        const tone = TONE[t.color ?? 'primary']
        return (
          <div
            key={t.id}
            role="status"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              padding: '13px 14px',
              background: tone.bg,
              border: `1px solid ${tone.border}`,
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-lg)',
              color: tone.fg,
              animation: 'oummaFade 240ms var(--ease-out) both',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 600,
                  fontSize: 14.5,
                }}
              >
                <span style={{ flexShrink: 0, display: 'inline-flex' }}>
                  {tone.icon}
                </span>
                <span>{t.title}</span>
              </div>
              {t.description && (
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--ink-2)',
                    marginTop: 4,
                    paddingLeft: 26,
                    wordBreak: 'break-word',
                  }}
                >
                  {t.description}
                </div>
              )}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              aria-label="Fermer"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit',
                opacity: 0.6,
                padding: 2,
              }}
            >
              <X size={16} />
            </button>
          </div>
        )
      })}
    </div>
  )
}
